// src/pages/Report/ReportesNormativos.js
import React, { useState } from "react";
import {
  Box,
  Paper,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Divider,
  Button,
  CircularProgress,
  Chip,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  OutlinedInput,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { descargarReporte202, descargarReporte2175 } from "../../api/ReportGeneratorApi";
import { exportReportePAI } from "../../api/pai/paiApi";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import FullScreenSpinner from "../../components/FullScreenSpinner";
import useAuth from "../../hooks/useAuth";
import { getEpsDisponibles } from "../../api/dashboardApi";
import sedeApi from "../../api/sedeApi";
import empresaApi from "../../api/empresaApi";

// ─── Catálogo de reportes normativos (quemado por ahora) ─────────────────────
const REPORTES_NORMATIVOS = [
  {
    id: "202",
    nombre: "Resolución 202",
    descripcion: "Minería de datos PyP — Minsalud",
    tag: "TXT",
    tagColor: "primary",
  },
  {
    id: "2175",
    nombre: "Resolución 2175",
    descripcion: "Atenciones en salud a menores de 18 años y gestantes",
    tag: "TXT",
    tagColor: "warning",
  },
  {
    id: "PAI",
    nombre: "Registro Diario PAI",
    descripcion: "Consolidado PAI — Vacunación",
    tag: "EXCEL/CSV",
    tagColor: "success",
  },
];

/* ─── Field: label arriba + input abajo (Estilo Users.js) ─── */
function Field({ lbl, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
      <label style={{ fontSize: "11px", color: "#64748b", fontWeight: "500" }}>
        {lbl}{required && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const inp = { flex: 1, padding: "5px 7px", borderRadius: "4px", border: "1px solid #cbd5e1", fontSize: "11px", boxSizing: "border-box", outline: "none", marginBottom: 0, width: "100%", background: "#fff", color: "#1e293b", fontFamily: "inherit", height: "28px" };

// ─── Panel de filtros por tipo de reporte ────────────────────────────────────
const PanelFiltros202 = ({ onDescargar }) => {
  const { sedes: authSedes } = useAuth();
  const [fechaIni, setFechaIni] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [sedesSeleccionadas, setSedesSeleccionadas] = useState([]);
  const [epsList, setEpsList] = useState([]);
  const [epsSeleccionadas, setEpsSeleccionadas] = useState([]);
  const [loading, setLoading] = useState(false);

  // Cargar EPS disponibles
  React.useEffect(() => {
    const fetchEps = async () => {
      try {
        const epsData = await getEpsDisponibles();
        const rawList = Array.isArray(epsData?.data) ? epsData.data : (Array.isArray(epsData) ? epsData : []);
        setEpsList(rawList.filter(e => e.eps));
      } catch (err) {
        console.warn("No se pudieron cargar las EPS", err);
      }
    };
    fetchEps();
  }, []);

  // Extraer empresas únicas
  const empresas = React.useMemo(() => {
    if (!authSedes) return [];
    const map = new Map();
    authSedes.forEach(s => {
      if (!map.has(s.id_empresa)) {
        map.set(s.id_empresa, { id: s.id_empresa, nombre: s.nombreEmpresa });
      }
    });
    return Array.from(map.values());
  }, [authSedes]);

  // Sedes filtradas por la empresa seleccionada
  const sedesFiltradas = React.useMemo(() => {
    if (!authSedes || !empresa) return [];
    return authSedes.filter(s => s.id_empresa === empresa);
  }, [authSedes, empresa]);

  // Selección automática si hay empresas/sedes
  React.useEffect(() => {
    if (empresas.length > 0 && !empresa) {
      setEmpresa(empresas[0].id);
    }
  }, [empresas, empresa]);

  React.useEffect(() => {
    if (sedesFiltradas.length > 0) {
      setSedesSeleccionadas(sedesFiltradas.map(s => s.id));
    } else {
      setSedesSeleccionadas([]);
    }
  }, [empresa, sedesFiltradas]);

  const handleDescargar = async () => {
    if (!fechaIni || !fechaFin) {
      toast.warning("Debe seleccionar un rango de fechas completo.");
      return;
    }

    setLoading(true);
    try {
      await onDescargar(fechaIni, fechaFin, empresa, sedesSeleccionadas.join(","), epsSeleccionadas.join(","));
      toast.success("Reporte Resolución 202 generado con éxito.");
    } catch (error) {
      console.error("Error al generar reporte 202:", error);
      toast.error(error?.message || "Error al generar el reporte.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <FullScreenSpinner text="Minerando datos y generando documento. Por favor, espere..." />}
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "var(--app-font-size)", mb: 2 }}>
            Seleccione el periodo para la minería de datos de la{" "}
            <strong>Resolución 202</strong>. El archivo generado es un{" "}
            <strong>.TXT</strong> con el formato exigido por el Ministerio de
            Salud.
          </Typography>
        </Box>

        <div style={{
          border: "1px solid #e2e8f0",
          borderRadius: "5px",
          padding: "12px 16px",
          background: "#f8fafc",
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1.5fr",
          gap: "10px 16px"
        }}>
          <Field lbl="Fecha inicio" required>
            <input type="date" style={inp} value={fechaIni} onChange={(e) => setFechaIni(e.target.value)} />
          </Field>

          <Field lbl="Fecha fin" required>
            <input type="date" style={inp} value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
          </Field>

          <Field lbl="Empresa" required>
            <select style={inp} value={empresa || ""} onChange={(e) => setEmpresa(e.target.value)}>
              {empresas.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.nombre}
                </option>
              ))}
            </select>
          </Field>

          <div style={{ gridColumn: "1 / span 2" }}>
            <Field lbl="Sedes" required>
              <FormControl fullWidth>
                <Select
                  multiple
                  value={sedesSeleccionadas}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSedesSeleccionadas(typeof value === 'string' ? value.split(',') : value);
                  }}
                  displayEmpty
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.length === 0 ? <span style={{color: "#94a3b8", fontSize: "11px"}}>Seleccione sedes...</span> : null}
                      {selected.map((value) => {
                        const sedeLabel = sedesFiltradas.find((s) => s.id === value)?.nombreSede || value;
                        return <Chip key={value} label={sedeLabel} size="small" sx={{ fontSize: "0.65rem", height: 18 }} />;
                      })}
                    </Box>
                  )}
                  sx={{
                    "& .MuiSelect-select": { padding: "4px 7px", minHeight: "18px", fontSize: "11px", backgroundColor: "#fff" },
                    fieldset: { borderColor: "#cbd5e1" },
                    "&.Mui-focused fieldset": { borderColor: "#3b82f6 !important", borderWidth: "1px !important" }
                  }}
                >
                  {sedesFiltradas.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={sedesSeleccionadas.indexOf(s.id) > -1} size="small" />
                      <ListItemText primary={s.nombreSede} primaryTypographyProps={{ variant: "body2", fontSize: "11px" }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Field>
          </div>

          <div style={{ gridColumn: "3 / span 1" }}>
            <Field lbl="EPS" required>
              <FormControl fullWidth>
                <Select
                  multiple
                  value={epsSeleccionadas}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEpsSeleccionadas(typeof value === 'string' ? value.split(',') : value);
                  }}
                  displayEmpty
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.length === 0 ? <span style={{color: "#94a3b8", fontSize: "11px"}}>Seleccione EPS...</span> : null}
                      {selected.map((value) => {
                        const epsLabel = epsList.find((e) => e.eps === value)?.descripcion || value;
                        return <Chip key={value} label={epsLabel} size="small" sx={{ fontSize: "0.65rem", height: 18 }} />;
                      })}
                    </Box>
                  )}
                  sx={{
                    "& .MuiSelect-select": { padding: "4px 7px", minHeight: "18px", fontSize: "11px", backgroundColor: "#fff" },
                    fieldset: { borderColor: "#cbd5e1" },
                    "&.Mui-focused fieldset": { borderColor: "#3b82f6 !important", borderWidth: "1px !important" }
                  }}
                >
                  {epsList.map((e) => (
                    <MenuItem key={e.eps} value={e.eps}>
                      <Checkbox checked={epsSeleccionadas.indexOf(e.eps) > -1} size="small" />
                      <ListItemText primary={e.descripcion} primaryTypographyProps={{ variant: "body2", fontSize: "11px" }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Field>
          </div>
        </div>

        <Divider />

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FileDownloadOutlinedIcon fontSize="small" />
              )
            }
            onClick={handleDescargar}
            disabled={loading}
            sx={{ fontSize: "var(--app-font-size)", minWidth: 160 }}
          >
            {loading ? "Generando..." : "Descargar TXT"}
          </Button>
        </Box>
      </Stack>
    </>
  );
};

// ─── Panel de filtros para Resolución 2175 ────────────────────────────────────
const PanelFiltros2175 = ({ onDescargar }) => {
  const { sedes: authSedes } = useAuth();
  const [fechaIni, setFechaIni] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [empresas, setEmpresas] = useState([]);
  const [todasLasSedes, setTodasLasSedes] = useState([]);
  const [sedesSeleccionadas, setSedesSeleccionadas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cargandoCatalogos, setCargandoCatalogos] = useState(true);

  // Cargar empresas y sedes activas desde API
  React.useEffect(() => {
    let isMounted = true;
    const fetchCatalogos = async () => {
      try {
        const [empRs, sedesRs] = await Promise.allSettled([
          empresaApi.getAllActivas(),
          sedeApi.getAll(),
        ]);

        let listaEmpresas = [];
        let listaSedes = [];

        if (empRs.status === "fulfilled" && Array.isArray(empRs.value?.data)) {
          listaEmpresas = empRs.value.data.map((e) => ({
            id: String(e.id),
            nombre: e.nombre,
          }));
        }

        if (sedesRs.status === "fulfilled" && Array.isArray(sedesRs.value?.data)) {
          // Filtrar únicamente sedes activas
          listaSedes = sedesRs.value.data.filter(
            (s) => s.estado === 1 || s.estado === true || s.estado === "1"
          );
        }

        // Fallback a authSedes si la API no devolvió empresas
        if (listaEmpresas.length === 0 && authSedes?.length > 0) {
          const map = new Map();
          authSedes.forEach((s) => {
            const empId = String(s.id_empresa);
            if (s.id_empresa && !map.has(empId)) {
              map.set(empId, {
                id: empId,
                nombre: s.nombreEmpresa || `Empresa ${s.id_empresa}`,
              });
            }
          });
          listaEmpresas = Array.from(map.values());
        }

        // Fallback a authSedes si la API no devolvió sedes
        if (listaSedes.length === 0 && authSedes?.length > 0) {
          listaSedes = authSedes
            .filter((s) => s.estado === undefined || s.estado === 1 || s.estado === true)
            .map((s) => ({
              id: s.id,
              nombreSede: s.nombreSede,
              id_empresa: String(s.id_empresa),
              estado: 1,
            }));
        }

        if (isMounted) {
          setEmpresas(listaEmpresas);
          setTodasLasSedes(listaSedes);
          if (listaEmpresas.length > 0) {
            setEmpresa(String(listaEmpresas[0].id));
          }
          setCargandoCatalogos(false);
        }
      } catch (err) {
        console.error("Error cargando catálogos para 2175:", err);
        if (isMounted) setCargandoCatalogos(false);
      }
    };

    fetchCatalogos();
    return () => {
      isMounted = false;
    };
  }, [authSedes]);

  // Sedes activas filtradas de la empresa seleccionada
  const sedesFiltradas = React.useMemo(() => {
    if (!empresa || todasLasSedes.length === 0) return [];
    return todasLasSedes.filter(
      (s) => String(s.id_empresa) === String(empresa)
    );
  }, [empresa, todasLasSedes]);

  // Al cambiar la empresa o actualizar las sedes filtradas, preseleccionar automáticamente todas las sedes activas
  React.useEffect(() => {
    if (sedesFiltradas.length > 0) {
      setSedesSeleccionadas(sedesFiltradas.map((s) => s.id));
    } else {
      setSedesSeleccionadas([]);
    }
  }, [sedesFiltradas]);

  const handleEmpresaChange = (e) => {
    const empId = e.target.value;
    setEmpresa(empId);
  };

  const handleToggleSelectAllSedes = () => {
    if (sedesSeleccionadas.length === sedesFiltradas.length) {
      setSedesSeleccionadas([]);
    } else {
      setSedesSeleccionadas(sedesFiltradas.map((s) => s.id));
    }
  };

  const handleDescargar = async () => {
    if (!fechaIni || !fechaFin) {
      toast.warning("Por favor seleccione fecha inicial y final");
      return;
    }
    if (!empresa) {
      toast.warning("Por favor seleccione una empresa");
      return;
    }
    if (sedesSeleccionadas.length === 0) {
      toast.warning("Por favor seleccione al menos una sede activa");
      return;
    }

    setLoading(true);
    try {
      await onDescargar(
        fechaIni,
        fechaFin,
        parseInt(empresa, 10),
        sedesSeleccionadas.join(",")
      );
      toast.success("Reporte Resolución 2175 descargado exitosamente");
    } catch (err) {
      toast.error(`Error al generar reporte: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <FullScreenSpinner message="Generando archivo plano Resolución 2175 de 2015..." />}
      <Stack spacing={2}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <Field lbl="Fecha inicial" required>
            <input
              type="date"
              value={fechaIni}
              onChange={(e) => setFechaIni(e.target.value)}
              style={inp}
            />
          </Field>
          <Field lbl="Fecha final" required>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              style={inp}
            />
          </Field>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px" }}>
          <Field lbl="Empresa" required>
            <select
              value={empresa}
              onChange={handleEmpresaChange}
              style={inp}
              disabled={cargandoCatalogos}
            >
              {cargandoCatalogos && <option value="">Cargando empresas...</option>}
              {!cargandoCatalogos && empresas.length === 0 && (
                <option value="">No hay empresas disponibles</option>
              )}
              {empresas.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.nombre}
                </option>
              ))}
            </select>
          </Field>

          <Field lbl="Sedes" required>
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={sedesSeleccionadas}
                onChange={(e) => {
                  const value = e.target.value;
                  setSedesSeleccionadas(typeof value === 'string' ? value.split(',') : value);
                }}
                displayEmpty
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.length === 0 ? (
                      <span style={{ color: "#94a3b8", fontSize: "11px" }}>
                        {cargandoCatalogos
                          ? "Cargando sedes..."
                          : sedesFiltradas.length === 0
                          ? "No hay sedes activas para esta empresa"
                          : "Seleccione sedes..."}
                      </span>
                    ) : (
                      selected.map((value) => {
                        const sedeLabel =
                          sedesFiltradas.find((s) => s.id === value)?.nombreSede || value;
                        return (
                          <Chip
                            key={value}
                            label={sedeLabel}
                            size="small"
                            sx={{ fontSize: "0.65rem", height: 18 }}
                          />
                        );
                      })
                    )}
                  </Box>
                )}
                sx={{
                  "& .MuiSelect-select": {
                    padding: "4px 7px",
                    minHeight: "18px",
                    fontSize: "11px",
                    backgroundColor: "#fff",
                  },
                  fieldset: { borderColor: "#cbd5e1" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3b82f6 !important",
                    borderWidth: "1px !important",
                  },
                }}
              >
                {sedesFiltradas.length > 0 && (
                  <MenuItem
                    onClick={handleToggleSelectAllSedes}
                    sx={{ py: 0.5, borderBottom: "1px solid #f1f5f9" }}
                  >
                    <Checkbox
                      checked={
                        sedesFiltradas.length > 0 &&
                        sedesSeleccionadas.length === sedesFiltradas.length
                      }
                      indeterminate={
                        sedesSeleccionadas.length > 0 &&
                        sedesSeleccionadas.length < sedesFiltradas.length
                      }
                      size="small"
                    />
                    <ListItemText
                      primary={
                        sedesSeleccionadas.length === sedesFiltradas.length
                          ? "Deseleccionar todas"
                          : "Seleccionar todas las sedes activas"
                      }
                      primaryTypographyProps={{
                        variant: "body2",
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#2563eb",
                      }}
                    />
                  </MenuItem>
                )}
                {sedesFiltradas.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Checkbox
                      checked={sedesSeleccionadas.indexOf(s.id) > -1}
                      size="small"
                    />
                    <ListItemText
                      primary={s.nombreSede}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontSize: "11px",
                      }}
                    />
                  </MenuItem>
                ))}
                {sedesFiltradas.length === 0 && (
                  <MenuItem disabled>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "11px", color: "#94a3b8" }}
                    >
                      {cargandoCatalogos
                        ? "Cargando sedes..."
                        : "No se encontraron sedes activas para esta empresa"}
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Field>
        </div>

        <Divider />

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FileDownloadOutlinedIcon fontSize="small" />
              )
            }
            onClick={handleDescargar}
            disabled={loading || cargandoCatalogos || sedesFiltradas.length === 0}
            sx={{ fontSize: "var(--app-font-size)", minWidth: 160 }}
          >
            {loading ? "Generando..." : "Descargar TXT"}
          </Button>
        </Box>
      </Stack>
    </>
  );
};

// ─── Panel de filtros para PAI ────────────────────────────────────
const PanelFiltrosPAI = ({ onDescargar }) => {
  const { sedes: authSedes } = useAuth();
  const [fechaIni, setFechaIni] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [sedesSeleccionadas, setSedesSeleccionadas] = useState([]);
  const [loadingXlsx, setLoadingXlsx] = useState(false);
  const [loadingCsv, setLoadingCsv] = useState(false);

  // Extraer empresas únicas
  const empresas = React.useMemo(() => {
    if (!authSedes) return [];
    const map = new Map();
    authSedes.forEach(s => {
      if (!map.has(s.id_empresa)) {
        map.set(s.id_empresa, { id: s.id_empresa, nombre: s.nombreEmpresa });
      }
    });
    return Array.from(map.values());
  }, [authSedes]);

  // Sedes filtradas por la empresa seleccionada
  const sedesFiltradas = React.useMemo(() => {
    if (!authSedes || !empresa) return [];
    return authSedes.filter(s => s.id_empresa === empresa);
  }, [authSedes, empresa]);

  // Selección automática si hay empresas/sedes
  React.useEffect(() => {
    if (empresas.length > 0 && !empresa) {
      setEmpresa(empresas[0].id);
    }
  }, [empresas, empresa]);

  React.useEffect(() => {
    if (sedesFiltradas.length > 0) {
      setSedesSeleccionadas(sedesFiltradas.map(s => s.id));
    } else {
      setSedesSeleccionadas([]);
    }
  }, [empresa, sedesFiltradas]);

  const handleDescargar = async (formato) => {
    if (!fechaIni || !fechaFin) {
      toast.warning("Debe seleccionar un rango de fechas completo.");
      return;
    }
    if (formato === "xlsx") setLoadingXlsx(true);
    else setLoadingCsv(true);

    try {
      const sedesQuery = sedesSeleccionadas.join(",");
      await onDescargar(fechaIni, fechaFin, empresa, sedesQuery, formato);
      toast.success(`Reporte PAI (${formato.toUpperCase()}) generado con éxito.`);
    } catch (error) {
      console.error(`Error al generar reporte PAI ${formato}:`, error);
      toast.error(error?.message || "Error al generar el reporte PAI.");
    } finally {
      if (formato === "xlsx") setLoadingXlsx(false);
      else setLoadingCsv(false);
    }
  };

  return (
    <>
      {(loadingXlsx || loadingCsv) && <FullScreenSpinner text="Minerando datos PAI y generando documento. Por favor, espere..." />}
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "var(--app-font-size)", mb: 2 }}>
            Seleccione el periodo para el reporte de{" "}
            <strong>Registro Diario PAI</strong>.
          </Typography>
        </Box>

        <div style={{
          border: "1px solid #e2e8f0",
          borderRadius: "5px",
          padding: "12px 16px",
          background: "#f8fafc",
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1.5fr",
          gap: "10px 16px"
        }}>
          <Field lbl="Fecha inicio" required>
            <input type="date" style={inp} value={fechaIni} onChange={(e) => setFechaIni(e.target.value)} />
          </Field>

          <Field lbl="Fecha fin" required>
            <input type="date" style={inp} value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
          </Field>

          <Field lbl="Empresa" required>
            <select style={inp} value={empresa || ""} onChange={(e) => setEmpresa(e.target.value)}>
              {empresas.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.nombre}
                </option>
              ))}
            </select>
          </Field>

          <div style={{ gridColumn: "1 / span 3" }}>
            <Field lbl="Sedes" required>
              <FormControl fullWidth>
                <Select
                  multiple
                  value={sedesSeleccionadas}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSedesSeleccionadas(typeof value === 'string' ? value.split(',') : value);
                  }}
                  displayEmpty
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.length === 0 ? <span style={{color: "#94a3b8", fontSize: "11px"}}>Seleccione sedes...</span> : null}
                      {selected.map((value) => {
                        const sedeLabel = sedesFiltradas.find((s) => s.id === value)?.nombreSede || value;
                        return <Chip key={value} label={sedeLabel} size="small" sx={{ fontSize: "0.65rem", height: 18 }} />;
                      })}
                    </Box>
                  )}
                  sx={{
                    "& .MuiSelect-select": { padding: "4px 7px", minHeight: "18px", fontSize: "11px", backgroundColor: "#fff" },
                    fieldset: { borderColor: "#cbd5e1" },
                    "&.Mui-focused fieldset": { borderColor: "#3b82f6 !important", borderWidth: "1px !important" }
                  }}
                >
                  {sedesFiltradas.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={sedesSeleccionadas.indexOf(s.id) > -1} size="small" />
                      <ListItemText primary={s.nombreSede} primaryTypographyProps={{ variant: "body2", fontSize: "11px" }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Field>
          </div>
        </div>

        <Divider />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="outlined"
            color="success"
            size="small"
            startIcon={
              loadingCsv ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FileDownloadOutlinedIcon fontSize="small" />
              )
            }
            onClick={() => handleDescargar("csv")}
            disabled={loadingCsv || loadingXlsx}
            sx={{ fontSize: "var(--app-font-size)", minWidth: 160 }}
          >
            {loadingCsv ? "Generando CSV..." : "Generar CSV"}
          </Button>

          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={
              loadingXlsx ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FileDownloadOutlinedIcon fontSize="small" />
              )
            }
            onClick={() => handleDescargar("xlsx")}
            disabled={loadingCsv || loadingXlsx}
            sx={{ fontSize: "var(--app-font-size)", minWidth: 160 }}
          >
            {loadingXlsx ? "Generando XLSX..." : "Generar XLSX"}
          </Button>
        </Box>
      </Stack>
    </>
  );
};

// ─── Mapa: id → componente de filtros ────────────────────────────────────────
const PANELES = {
  "202": (onDescargar) => <PanelFiltros202 onDescargar={onDescargar} />,
  "2175": (onDescargar) => <PanelFiltros2175 onDescargar={onDescargar} />,
  "PAI": (onDescargar) => <PanelFiltrosPAI onDescargar={onDescargar} />,
};

// ─── Mapa: id → función de descarga ──────────────────────────────────────────
const DESCARGAS = {
  "202": (fechaIni, fechaFin, empresa, sede, eps) => descargarReporte202(fechaIni, fechaFin, empresa, sede, eps),
  "2175": (fechaIni, fechaFin, empresa, sede) => descargarReporte2175(fechaIni, fechaFin, empresa, sede),
  "PAI": async (fechaIni, fechaFin, empresa, sede, formato) => {
    const res = await exportReportePAI({
      fecha_ini: fechaIni,
      fecha_fin: fechaFin,
      empresa: empresa,
      sedes: sede,
      formato: formato
    });
    saveAs(res.blob, res.filename);
  },
};

// ─── Página principal ─────────────────────────────────────────────────────────
const ReportesNormativos = () => {
  const [reporteSeleccionado, setReporteSeleccionado] = useState(null);

  const panelActivo = reporteSeleccionado
    ? PANELES[reporteSeleccionado.id]?.(DESCARGAS[reporteSeleccionado.id])
    : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          height:
            "calc(100vh - (var(--navbar-height) + var(--footer-height)))",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Paper
          sx={{
            flex: 1,
            m: 0.5,
            display: "flex",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Stack
            direction="row"
            sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}
          >
            {/* ── Panel izquierdo: lista de resoluciones ── */}
            <Box
              sx={{
                width: 240,
                borderRight: "1px solid #ddd",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box sx={{ p: 1.5, pb: 1, borderBottom: "1px solid #eee" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontSize: "0.68rem",
                  }}
                >
                  Resoluciones disponibles
                </Typography>
              </Box>
              <List dense sx={{ flex: 1, overflow: "auto", py: 0.5 }}>
                {REPORTES_NORMATIVOS.map((r) => (
                  <ListItemButton
                    key={r.id}
                    selected={reporteSeleccionado?.id === r.id}
                    onClick={() => setReporteSeleccionado(r)}
                    sx={{ py: 0.75, px: 1.5, borderRadius: 1, mx: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <DescriptionOutlinedIcon
                        fontSize="small"
                        color={
                          reporteSeleccionado?.id === r.id
                            ? "primary"
                            : "disabled"
                        }
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={r.nombre}
                      secondary={r.descripcion}
                      primaryTypographyProps={{
                        variant: "body2",
                        sx: { fontSize: "var(--app-font-size)", fontWeight: reporteSeleccionado?.id === r.id ? 600 : 400 },
                      }}
                      secondaryTypographyProps={{
                        variant: "caption",
                        sx: { fontSize: "calc(var(--app-font-size) * 0.85)" },
                      }}
                    />
                    <Chip
                      label={r.tag}
                      size="small"
                      color={r.tagColor}
                      variant="outlined"
                      sx={{ fontSize: "0.65rem", height: 18, ml: 0.5 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>

            {/* ── Panel derecho: filtros del reporte seleccionado ── */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                p: 2.5,
              }}
            >
              {reporteSeleccionado ? (
                <>
                  {/* Encabezado */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 2 }}
                  >
                    <AssignmentOutlinedIcon color="primary" />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        fontSize: "var(--app-font-size)",
                      }}
                    >
                      {reporteSeleccionado.nombre}
                    </Typography>
                    <Chip
                      label={`Formato ${reporteSeleccionado.tag}`}
                      size="small"
                      color={reporteSeleccionado.tagColor}
                      sx={{ fontSize: "0.65rem" }}
                    />
                  </Stack>
                  <Divider sx={{ mb: 2.5 }} />

                  {/* Filtros del reporte */}
                  <Box sx={{ maxWidth: 480 }}>{panelActivo}</Box>
                </>
              ) : (
                // Estado vacío
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    color: "text.disabled",
                  }}
                >
                  <AssignmentOutlinedIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "var(--app-font-size)" }}
                  >
                    Seleccione una resolución para ver sus opciones de descarga
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default ReportesNormativos;
